import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { List, arrayMove } from "react-movable";
import Button from "shared/components/buttons/Button";
import { ICONS } from "shared/components/Icon";
import ChapterItem from "components/ChapterItem";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Modal from "components/Modal";
import ChapterForm from "./ChapterForm";
import OkWarning from "shared/components/OkWarning";
import bookChapterRemove from "redux/actions/books/bookChapterRemove";
import { snackbarError } from "shared/redux/actions/Snackbar";
import bookChapterSort from "redux/actions/books/bookChaptersSort";
import { bookSetDefaultContent } from "redux/actions/books/bookSetDefaultContent";
import "./style.scss";

const Chapters = () => {
    const { t } = useTranslation();
    const { osbn } = useParams();
    const dispatch = useDispatch();

    const { books } = useSelector((state) => state.books);
    const book = books[osbn];
    const { chapters } = book;
    const keys = React.useMemo(() => Object.keys(chapters), [chapters]);
    const [orderedKeys, setOrderedKeys] = useState(keys);

    useEffect(() => {
        setOrderedKeys(keys);
    }, [keys]);

    useEffect(() => {

        if (book.startItem == null) {

            const chapterKeys = Object.keys(chapters);
            const singleChapterKey = chapterKeys.length === 1 ? chapterKeys[0] : null;

            if (singleChapterKey) {
                // dispatch(bookSetDefaultContent(osbn, singleChapterKey, []));

            }

        }
    }, [chapters]);


    const stripTypename = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(stripTypename);
        } else if (obj && typeof obj === "object") {
            const cleaned = {};
            for (const key in obj) {
                if (key === "__typename") continue;
                cleaned[key] = stripTypename(obj[key]);
            }
            return cleaned;
        }
        return obj;
    };
    const [showChapterModal, setShowChapterModal] = useState(false);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [removeChapter, setRemoveChapter] = useState(false);

    const onEdit = (chapterId) => {

        setShowChapterModal(true);
        setSelectedChapter(chapterId);
    };

    const onRemove = (chapterId) => {
        const searchString = JSON.stringify(book);
        const exp = new RegExp(chapterId, "g");
        const isConnected = (searchString.match(exp) || []).length >= 3;

        if (book.defaultChapters?.includes(chapterId)) {
            dispatch(snackbarError(t("book.chapters.delete.inUse.defaultChapter")));
        } else if (book.startItem === chapterId) {
            dispatch(snackbarError(t("book.chapters.delete.inUse.startItem")));
        } else if (isConnected) {
            dispatch(snackbarError(t("book.chapters.delete.inUse.chapters")));
        } else {
            setRemoveChapter(chapterId);
        }
    };

    const suppressSpace = (e) => {
        if (e.code === "Space" || e.keyCode === 32) {
            e.stopPropagation();
        }
    };

    const renderConfirmRemove = () => {
        return (
            <OkWarning
                title={t("book.chapter.remove.confirm.title")}
                message={t("book.chapter.remove.confirm.message")}
                onConfirm={() => {
                    dispatch(bookChapterRemove(osbn, removeChapter));
                    setRemoveChapter(false);
                }}
                onCancel={() => setRemoveChapter(false)}
            />
        );
    };

    return (
        <div className="section-chapters">
            <div className="section-title">
                <h2>{t("book.edit.chapters.title")}</h2>
                <Button
                    label={t("book.edit.chapters.add")}
                    iconRight={ICONS.PLUS}
                    onClick={() => {
                        setSelectedChapter(null);
                        setShowChapterModal(true);
                    }}
                />
            </div>

            <div className="divider" />

            {orderedKeys.length > 0 ? (
                <List
                    values={orderedKeys.filter((key) => chapters[key])}
                    disableAriaLive={true}
                    onChange={({ oldIndex, newIndex }) => {
                        const updatedKeys = arrayMove(orderedKeys, oldIndex, newIndex);
                        setOrderedKeys(updatedKeys);
                        dispatch(bookChapterSort(osbn, updatedKeys));
                        dispatch(bookSetDefaultContent(osbn, updatedKeys[0], book.defaultChapters));

                    }}
                    renderList={({ children, props, isDragged }) => (
                        <ul
                            {...props}
                            style={{
                                padding: "0em 0em 1em 0em",
                                cursor: isDragged ? "grabbing" : "inherit",
                            }}
                        >
                            {children}
                        </ul>
                    )}
                    renderItem={({ value, props, index }) => {


                        return (
                            <li
                                {...props}
                                className={`list-movable-${index}`}
                                onKeyDown={suppressSpace}
                                style={{
                                    ...props.style,
                                    listStyleType: "none",
                                }}
                            >
                                <ChapterItem
                                    key={value}
                                    zIndexValue={`list-movable-${index}`}
                                    index={index + 1}
                                    osbn={osbn}
                                    defaultChapters={book.defaultChapters}
                                    bookStartItem={book.startItem}
                                    chapterId={value}
                                    bookId={book.id}
                                    chapter={chapters[value]}
                                    onEdit={onEdit}
                                    onRemove={onRemove}
                                />
                            </li>
                        );
                    }}
                />
            ) : (
                <span>{t("book.edit.chapters.empty")}</span>
            )}

            {showChapterModal && (
                <Modal
                    title={t("chapter.modal.title")}
                    onClose={() => setShowChapterModal(false)}
                    tabs={[
                        {
                            tabName: "info",
                            component: (
                                <ChapterForm
                                    chapter={selectedChapter ? chapters[selectedChapter] : null}
                                    onClose={() => {
                                        setShowChapterModal(false)
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            )}

            {removeChapter && renderConfirmRemove()}
        </div>
    );
};

export default Chapters;
